import React, { useState } from "react";
import { GoogleSignIn } from "@/components/GoogleSignIn";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { REGISTRATION_PATHS, label } from '@/lib/subjects';

const Shell = ({ title, sub, children }) => (
  <div className="grain-bg min-h-[calc(100vh-72px)] flex items-center py-16">
    <div className="max-w-md mx-auto px-4 w-full">
      <div className="text-center mb-8">
        <Link to="/" className="inline-block"><Logo /></Link>
        <h1 className="font-serif text-3xl font-bold text-[#2A2521] mt-6">{title}</h1>
        <p className="font-sans text-[#6E6258] mt-1.5">{sub}</p>
      </div>
      <div className="bg-[#FFFDF8] rounded-md border border-[#E2DACB] p-6 sm:p-8">{children}</div>
    </div>
  </div>
);

export function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await login(form.email, form.password);
      navigate(location.state?.from || "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Shell title={t("auth.signInTitle")} sub={t("auth.signInSub")}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label className="text-sm text-[#4A443D] mb-1.5 block">{t("auth.email")}</Label>
          <Input data-testid="login-email" type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white border-[#E2DACB]" />
        </div>
        <div>
          <Label className="text-sm text-[#4A443D] mb-1.5 block">{t("auth.password")}</Label>
          <Input data-testid="login-password" type="password" required value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-white border-[#E2DACB]" />
        </div>
        {error && <p data-testid="login-error" className="text-sm text-[#B3261E]">{error}</p>}
        <Button type="submit" disabled={busy} data-testid="login-submit"
          className="w-full h-11 rounded-full bg-[#557448] hover:bg-[#46633b] text-[#F8F5EA]">
          {busy ? "…" : t("auth.login")}
        </Button>
      </form>
      <GoogleSignIn onSuccess={() => navigate(location.state?.from || '/dashboard', { replace: true })} />
      <p className="text-sm text-[#6E6258] text-center mt-5">
        {t("auth.noAccount")} <Link to="/register" className="text-[#557448] font-medium hover:underline">{t("auth.register")}</Link>
      </p>
    </Shell>
  );
}

export function Register() {
  const { t, lang } = useLang();
  const [path, setPath] = useState('citizen');
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await register(form.email, form.password, form.name, path);
      navigate(path === 'citizen' ? '/dashboard' : `/list-your-farm?registration_path=${encodeURIComponent(path)}`, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Shell title={t("auth.registerTitle")} sub={t("auth.registerSub")}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <fieldset><legend className="text-sm mb-2">{lang === 'it' ? 'Come vuoi partecipare?' : 'How would you like to participate?'}</legend>
          <div className="grid gap-2">{REGISTRATION_PATHS.map(r => <label key={r[0]} className="flex items-center gap-2 border rounded-md p-3 text-sm">
            <input type="radio" name="registration-path" value={r[0]} checked={path === r[0]} onChange={() => setPath(r[0])} />{label(r, lang)}
          </label>)}</div>
          <p className="text-xs mt-2">{lang === 'it' ? 'L’account è gratuito. Dopo la registrazione puoi compilare la scheda specifica del soggetto; il cittadino può partecipare direttamente.' : 'The account is free. After registration, complete the subject’s specific profile; citizens can participate directly.'}</p>
        </fieldset>
        <div>
          <Label className="text-sm text-[#4A443D] mb-1.5 block">{t("auth.name")}</Label>
          <Input data-testid="register-name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-[#E2DACB]" />
        </div>
        <div>
          <Label className="text-sm text-[#4A443D] mb-1.5 block">{t("auth.email")}</Label>
          <Input data-testid="register-email" type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white border-[#E2DACB]" />
        </div>
        <div>
          <Label className="text-sm text-[#4A443D] mb-1.5 block">{t("auth.password")}</Label>
          <Input data-testid="register-password" type="password" required minLength={8} value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-white border-[#E2DACB]" />
          <p className="text-xs text-[#9A8F80] mt-1">{t("auth.passwordHint")}</p>
        </div>
        {error && <p data-testid="register-error" className="text-sm text-[#B3261E]">{error}</p>}
        <Button type="submit" disabled={busy} data-testid="register-submit"
          className="w-full h-11 rounded-full bg-[#557448] hover:bg-[#46633b] text-[#F8F5EA]">
          {busy ? "…" : t("auth.register")}
        </Button>
      </form>
      <GoogleSignIn intent="register" registrationPath={path} onSuccess={data => navigate(data.created && path !== 'citizen' ? `/list-your-farm?registration_path=${encodeURIComponent(path)}` : '/dashboard', { replace: true })} />
      <p className="text-sm text-[#6E6258] text-center mt-5">
        {t("auth.haveAccount")} <Link to="/login" className="text-[#557448] font-medium hover:underline">{t("auth.login")}</Link>
      </p>
    </Shell>
  );
}


